// Contextual acting-seat resolution for the SOLO pilot (Phase D2). One player
// drives BOTH seats over one socket, so every outgoing match command/intent
// names its acting seat on the wire envelope. This pure resolver decides which
// seat a given message acts FOR:
//
//   1. an explicit seat on the intent / command payload (cast, turn, choose,
//      DRAW, mulligan, pregame, pile-reveal, shuffle, REQUEST_ROLL…);
//   2. combat derivation. Declare/shot/assign act AS the attacker's
//      controller, defend/intercept answer FOR the seat OPPOSITE the open
//      attacker, offer-intercept acts as the mover's controller;
//   3. the subject card's live controller from the authoritative mirror
//      (movement / ability / automation trigger / manual gestures);
//   4. fallback: the piloted seat.
//
// SET_FIRST_PLAYER is special-cased: only the roll-off WINNER may commit the
// pick, and the payload seat is the CHOSEN first player. Not the actor.
//
// Pure: no React, no sockets. GameBoard._soloActingSeat delegates here with
// the live mirror state + piloted seat.

const seatOk = (s) => (s === 'p1' || s === 'p2' ? s : null);

export function resolveSoloActingSeat({ msg, kind, mirror, pilotSeat = 'p1' }) {
  const controllerOf = (id) => (
    id ? seatOk(mirror?.cards?.[id]?.controller || mirror?.cards?.[id]?.owner) : null
  );

  if (kind === 'intent') {
    const it = msg || {};
    if (seatOk(it.seat)) return it.seat;
    if (it.domain === 'combat') {
      // offer-intercept acts as the MOVER's controller (a move-without-attack).
      const moverSeat = controllerOf(it.moverId);
      if (moverSeat) return moverSeat;
      // The live combat record stores the attacker as { attacker: { instanceId } }
      // (matchStateShape); reading the non-existent `combat.attackerId` stamped
      // solo defend/intercept replies with the PILOT seat, which the server
      // authz-rejected. Wedging the fight behind "Resolving...".
      const attackerSeat = controllerOf(
        it.attackerId || mirror?.combat?.attacker?.instanceId || mirror?.combat?.attackerId,
      );
      if (it.kind === 'defend' || it.kind === 'intercept') {
        // The answering seat is the one OPPOSITE the open attacker.
        if (attackerSeat) return attackerSeat === 'p1' ? 'p2' : 'p1';
      } else if (attackerSeat) {
        return attackerSeat; // declare / shot / assign
      }
    }
    const subject = controllerOf(it.cardId || it.sourceId);
    if (subject) return subject;
  } else {
    const cmd = msg || {};
    if (cmd.type === 'SET_FIRST_PLAYER') {
      const winner = seatOk(mirror?.rollOff?.winnerSeat);
      if (winner) return winner;
    }
    // PASS_TURN always acts AS the seat whose turn it is. The pilot's
    // perspective pointer (pilotSeat) can lag behind the authoritative turn
    // (restored blob, mulligan hand-off), and a mis-stamped pass is a silent
    // server no-op.
    if (cmd.type === 'PASS_TURN') {
      const turnSeat = seatOk(mirror?.phase?.currentTurn);
      if (turnSeat) return turnSeat;
    }
    if (seatOk(cmd.payload?.seat)) return cmd.payload.seat;
    const subject = controllerOf(cmd.payload?.cardId);
    if (subject) return subject;
  }
  return seatOk(pilotSeat) || 'p1';
}
